import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
  } from 'recharts';
import axios from 'axios';
import { deviceList } from '../../services/deviceService';
import './DashboardRedesign.css';
import {
    fetchEmergencyAlerts,
    fetchEmergencyCount,
    checkEmergencyAlert,
    closeEmergencyAlert,
} from '../../services/dashboardService';
import OccurModal from './OccurModal';
import ProcessModal from './ProcessModal';
import { useMessage } from '../../contexts/MessageContext';


// 곡성군 11개 읍면
const regions = [
    '곡성읍', '오곡면', '삼기면', '석곡면',
    '목사동면', '죽곡면', '고달면', '옥과면',
    '입면', '겸면', '오산면',
];



function DashboardRedesign() {
    const {
        setReceiveHandler,
        forceOpenModalType,
        setForceOpenModalType,
    } = useMessage();
    const [users, setUsers] = useState([]);
    const [userLoading, setUserLoading] = useState(true);
    const [userError, setUserError] = useState('');
    const totalUsers = users.length;
    const riskLevels = [
        { name: '고위험군', className: 'risk-high' },
        { name: '중위험군', className: 'risk-medium' },
        { name: '저위험군', className: 'risk-low' },
        ].map((risk) => ({
        ...risk,
        count: users.filter(
            (user) => user.sev_name === risk.name
        ).length,
    }));
    useEffect(() => {
        let active = true;

        const loadUsers = async () => {
            try {
            const token = localStorage.getItem('token');

            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/user`,
                {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                }
            );

            if (!active) return;

            if (!Array.isArray(response.data.user)) {
                throw new Error('사용자 목록 형식 오류');
            }

            setUsers(response.data.user);
            } catch (error) {
            if (!active) return;

            console.error('대시보드 사용자 조회 실패:', error);
            setUserError('사용자 데이터를 불러오지 못했습니다.');
            } finally {
            if (active) setUserLoading(false);
            }
        };

        loadUsers();

        return () => {
            active = false;
        };
    }, []);
    const [selectedDevice, setSelectedDevice] = useState('플러그');
    const [devices, setDevices] = useState([]);
    // 실제 API 기준 장비 이상 사용자
    
const abnormalDevices = devices
    .filter((device) => {
        const plugAbnormal =
            device.plug_id &&
            Number(device.plug_status) === 0;

        const switchAbnormal =
            device.swich_id &&
            Number(device.swich_status) === 0;

        const motionAbnormal =
            device.motion_id &&
            Number(device.motion_status) === 0;

        return plugAbnormal || switchAbnormal || motionAbnormal;
    })
    .map((device) => {
        const getStatus = (installed, status) => {
            if (!installed) return '미설치';
            if (status == null) return '확인 필요';
            return Number(status) === 1 ? '정상' : '비정상';
        };

        return {
            ...device,
            id: device.id,
            user: device.name || '-',
            location: device.address || '-',
            plug: getStatus(device.plug_id, device.plug_status),
            switch: getStatus(device.swich_id, device.swich_status),
            sensor: getStatus(device.motion_id, device.motion_status),
        };
    });

	//
    const installedDevices = {
        플러그: devices.length,
        스위치: devices.length,
        감지기: null,
      };
    const [deviceLoading, setDeviceLoading] = useState(true);
    const [deviceError, setDeviceError] = useState('');

    useEffect(() => {
        let active = true;

        const loadDevices = async () => {
            try {
                const token = localStorage.getItem('token');
                const data = await deviceList(token);

                if (!active) return;

                if (!data.success || !Array.isArray(data.device)) {
                    throw new Error(data.message || '장비 조회 실패');
                }

                setDevices(data.device);
            } catch (error) {
                if (!active) return;

                console.error('대시보드 장비 조회 실패:', error);
                setDeviceError('장비 데이터를 불러오지 못했습니다.');
            } finally {
                if (active) setDeviceLoading(false);
            }
        };

        loadDevices();

        return () => {
            active = false;
        };
    }, []);

    // 읍면별 장비 상태 집계
    const chartData = regions.map((region) => {
        const regionDevices = devices.filter((device) => {
            const addressRegion =
                device.address?.trim().split(/\s+/)[0];

            return addressRegion === region;
        });

        // 감지기는 아직 실제 API 연동 전
        if (selectedDevice === '감지기') {
            return { region, normal: 0, abnormal: 0 };
        }

        const statusKey =
            selectedDevice === '플러그'
                ? 'plug_status'
                : 'swich_status';

        const normal = regionDevices.filter(
            (device) =>
                device[statusKey] === 1 ||
                device[statusKey] === '1'
        ).length;

        const abnormal = regionDevices.filter(
            (device) =>
                device[statusKey] === 0 ||
                device[statusKey] === '0'
        ).length;

        return {
            region,
            normal,
            abnormal,
        };
    });

    const totalNormal = chartData.reduce(
        (sum, item) => sum + item.normal,
        0
    );

    const totalAbnormal = chartData.reduce(
        (sum, item) => sum + item.abnormal,
        0
    );

    const totalClassified = totalNormal + totalAbnormal;

    const [emergencyOccur, setEmergencyOccur] = useState(null);
    const [emergencyDone, setEmergencyDone] = useState(null);
    const [emergencyOccurList, setEmergencyOccurList] = useState([]);
    const [emergencyModalType, setEmergencyModalType] = useState(null);
    // FCM 알림 팝업에서 요청한 모달 열기
    useEffect(() => {
        if (!forceOpenModalType) return;

        if (
            forceOpenModalType === 'occur' ||
            forceOpenModalType === 'process'
        ) {
            setEmergencyModalType(forceOpenModalType);
            setForceOpenModalType(null);
        }
    }, [forceOpenModalType, setForceOpenModalType]);
    const [emergencyLoading, setEmergencyLoading] = useState(true);
    const [emergencyError, setEmergencyError] = useState('');
    const [emergencyEndList, setEmergencyEndList] = useState([]);

    // 응급상황 조회
    const loadEmergency = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) return;

        try {
            const [alerts, count] = await Promise.all([
                fetchEmergencyAlerts(token),
                fetchEmergencyCount(token),
            ]);

            if (
                !alerts.success ||
                !Array.isArray(alerts.user_alert) ||
                !count.success ||
                count.emergency_end_count == null
            ) {
                throw new Error('응급상황 API 응답 오류');
            }

            const occurItems = alerts.user_alert
                .filter((item) => item.incident_status !== 'CLOSED')
                .map((item) => ({
                    ...item,
                    gender: item.sex,
                    status: item.pec,
                }));

            const endItems = alerts.user_alert
                .filter((item) => item.incident_status === 'CLOSED')
                .map((item) => ({
                    ...item,
                    gender: item.sex,
                    status: item.pec,
                }));

            setEmergencyOccurList(occurItems);
            setEmergencyEndList(endItems);

            setEmergencyOccur(occurItems.length);
            setEmergencyDone(count.emergency_end_count);
            setEmergencyError('');

        } catch (error) {
            console.error('응급상황 현황 조회 실패:', error);
            setEmergencyError('응급상황 조회 실패');
        } finally {
            setEmergencyLoading(false);
        }
    }, []);

    // 최초 진입 및 FCM 수신 시 응급상황 갱신
    useEffect(() => {
        loadEmergency();

        setReceiveHandler(() => {
            console.log('[Dashboard] FCM 메시지 수신 → 응급상황 재조회');
            loadEmergency();
        });

        return () => {
            setReceiveHandler(null);
        };
    }, [loadEmergency, setReceiveHandler]);

	// 발생현황 모달이 열려 있는 동안 5초마다 최신 정보 조회
useEffect(() => {
    if (emergencyModalType !== 'occur') return;

    // 모달을 열 때 즉시 조회
    loadEmergency();

    // 모달이 열려 있는 동안 5초 간격으로 갱신
    const intervalId = setInterval(() => {
        loadEmergency();
    }, 5000);

    return () => {
        clearInterval(intervalId);
    };
}, [emergencyModalType, loadEmergency]);

        //필터링
        const navigate = useNavigate();

        const openRegionUsers = (region) => {
            if (!regions.includes(region)) return;

            const departmentName =
                region === '곡성읍'
                ? '곡성읍사무소'
                : `${region}사무소`;

            const params = new URLSearchParams({
                tab: 'alluser',
                region: departmentName,
                page: '1',
            });

            navigate(`/user?${params.toString()}`);
        };

        // 장비 이상 사용자 클릭 시 장비 상세페이지 이동
        const openDeviceDetail = (device) => {
            if (!device?.id) return;

            navigate(`/device/${device.id}`, {
            state: { device },
            });
        };

    return (
    <div className="safemap-redesign">
        {/* 읍면별 사용자 조회 */}
        <div className="dashboard-top-controls">
        <label htmlFor="dashboard-region">
            읍면별 사용자 조회
        </label>

        <select
            id="dashboard-region"
            defaultValue=""
            onChange={(e) => {
            const region = e.target.value;

            if (region) {
                openRegionUsers(region);
            }
            }}
        >
            <option value="">읍면 선택</option>

            {regions.map((region) => (
            <option key={region} value={region}>
                {region}
            </option>
            ))}
        </select>
        </div>
      <div className="dashboard-grid">

        {/* 응급상황 현황 */}
        <section className="panel">
        <h3>응급상황 현황</h3>

        <div className="stat-grid">
            <div
                className="stat-box"
                onClick={() => setEmergencyModalType('occur')}
                style={{ cursor: 'pointer' }}
                title="클릭하여 발생 현황 보기"
            >
            <span>발생 현황</span>
            <strong className="danger">
                {emergencyLoading
                ? '...'
                : emergencyError
                    ? '-'
                    : emergencyOccur}
            </strong>
            </div>

            <div className="stat-box"
              onClick={() => setEmergencyModalType('process')}
              style={{ cursor: 'pointer' }}
              title="클릭하여 상황 종료 내역 보기"
            >
            <span>상황 종료</span>
            <strong>
                {emergencyLoading
                ? '...'
                : emergencyError
                    ? '-'
                    : emergencyDone}
            </strong>
            </div>
        </div>

        {emergencyError && (
            <p className="mock-notice">{emergencyError}</p>
        )}
        </section>

        {/* 기기 설치 현황 */}
        <section className="panel">
          <h3>기기 설치 현황</h3>

          <div className="stat-grid three">
            {Object.entries(installedDevices).map(([name, count]) => (
                <div className="stat-box" key={name}>
                    <span>{name}</span>
                    <strong>
                    {deviceLoading
                        ? '...'
                        : deviceError
                        ? '-'
                        : count === null
                            ? '연동 전'
                            : `${count} 대`}
                    </strong>
                </div>
                ))}
          </div>
        </section>

        {/* 위험등급별 사용자 현황 */}
        <section className="panel risk-panel">
        <h3>위험등급별 사용자 현황</h3>

        {userLoading ? (
        <p>사용자 데이터를 불러오는 중...</p>
        ) : userError ? (
        <p className="danger">{userError}</p>
        ) : (
        <>
            <div className="risk-summary">
            <span>전체 사용자</span>
            <strong>{totalUsers}명</strong>
            </div>
            <div><p><span></span></p></div>
            <div className="risk-progress">
            {riskLevels.map((risk) => (
                <div
                key={risk.name}
                className={`risk-segment ${risk.className}`}
                style={{
                    width: totalUsers > 0
                    ? `${(risk.count / totalUsers) * 100}%`
                    : '0%',
                }}
                title={`${risk.name}: ${risk.count}명`}
                />
            ))}
            </div>

            <div className="risk-cards">
            {riskLevels.map((risk) => (
                <div className="risk-card" key={risk.name}>
                <span className={`risk-dot ${risk.className}`} />
                <span>{risk.name}</span>
                <strong>{risk.count}명</strong>
                </div>
            ))}
            </div>
        </>
        )}
        </section>

        {/* 장비 이상 사용자 현황 */}
        <section className="panel">
        <h3>장비 이상 사용자 현황
        {!deviceLoading && !deviceError && ` (${abnormalDevices.length}건)`}
        </h3>

        <div className="abnormal-table-scroll">
            <table className="abnormal-table">
            <thead>
                <tr>
                <th>어르신</th>
                <th>주  소</th>
                <th>플러그</th>
                <th>스위치</th>
                <th>감지기</th>
                </tr>
            </thead>

            <tbody>
                {deviceLoading ? (
                    <tr>
                    <td colSpan={5}>장비 데이터를 불러오는 중...</td>
                    </tr>
                ) : deviceError ? (
                    <tr>
                    <td colSpan={5}>{deviceError}</td>
                    </tr>
                ) : abnormalDevices.length === 0 ? (
                    <tr>
                    <td colSpan={5}>장비 이상 사용자가 없습니다.</td>
                    </tr>
                ) : (
                    abnormalDevices.map((device) => (
                    <tr key={device.id}
                        onClick={() => openDeviceDetail(device)}
                        style={{ cursor: 'pointer' }}
                        title="클릭하여 장비 상세정보 보기"
                    >
                        <td>{device.user}</td>
                        <td>{device.location}</td>

                        {['plug', 'switch', 'sensor'].map((type) => (
                        <td key={type}>
                            <span
                            className={
                                device[type] === '정상'
                                ? 'status-normal'
                                : device[type] === '비정상'
                                    ? 'status-danger'
                                    : 'status-uninstalled'
                            }
                            >
                            {device[type]}
                            </span>
                        </td>
                        ))}
                    </tr>
                    ))
                )}
            </tbody>
            </table>
        </div>
        </section>

      </div>
        {/* 읍면별 장비 설치 및 비정상 현황 */}
        <section className="panel chart-panel">
        <div className="panel-heading">
            <h3>읍면별 장비 상태 현황 </h3>

            <select
            value={selectedDevice}
            onChange={(e) => setSelectedDevice(e.target.value)}
            >
            {['플러그', '스위치', '감지기'].map((name) => (
                <option key={name} value={name}>
                    {name}
                </option>
            ))}
            </select>
        </div>

        <div className="chart-summary">
            {deviceLoading ? (
                <span>장비 데이터를 불러오는 중...</span>
            ) : deviceError ? (
                <span className="danger">{deviceError}</span>
            ) : selectedDevice === '감지기' ? (
                <span>감지기는 현재 연동 전입니다.</span>
            ) : (
                <>
                    <span>
                        상태 집계 <strong>{totalClassified}대</strong>
                    </span>
                    <span>
                        정상 <strong>{totalNormal}대</strong>
                    </span>
                    <span>
                        비정상{' '}
                        <strong className="danger">
                            {totalAbnormal}대
                        </strong>
                    </span>
                </>
            )}
        </div>

        <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
            <BarChart
                data={chartData}
                margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
            >
                <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                />

                <XAxis
                dataKey="region"
                tick={{ fontSize: 12 }}
                />

                <YAxis allowDecimals={false} />

                <Tooltip />
                <Legend />

                <Bar
                    dataKey="normal"
                    name="정상"
                    stackId="devices"
                    fill="#5b8def"
                    cursor="pointer"
                    onClick={(entry) => {
                        const region = entry?.region ?? entry?.payload?.region;
                        if (region) openRegionUsers(region);
                    }}
                />

                <Bar
                    dataKey="abnormal"
                    name="비정상"
                    stackId="devices"
                    fill="#ef7777"
                    cursor="pointer"
                    onClick={(entry) => {
                        const region = entry?.region ?? entry?.payload?.region;
                        if (region) openRegionUsers(region);
                    }}
                />
            </BarChart>
            </ResponsiveContainer>
        </div>
        </section>


      <p className="mock-notice">
      ※ 하단 읍면별 플러그·스위치 상태는 실제 API 조회 데이터입니다.
        상태값 1은 정상, 0은 비정상으로 집계합니다.
        사무실 테스트 장비는 읍면별 집계에서 제외됩니다.
        감지기 및 상단 현황은 아직 실제 데이터 연동 전입니다.
      </p>
        {emergencyModalType === 'occur' &&(
            <OccurModal
                data={emergencyOccurList}
                onReceive={async (alertId) => {
                    const token = localStorage.getItem('token');
                    if (!token) {
                        window.alert('로그인 정보가 없습니다.');
                        return;
                    }

                    const result = await checkEmergencyAlert(alertId, token);

                    if (result.success) {
                        await loadEmergency();
                    } else {
                        window.alert(result.message || '응급상황 접수 실패');
                    }
                }}
                onEnd={async (alertId) => {
                    const token = localStorage.getItem('token');
                    if (!token) {
                        window.alert('로그인 정보가 없습니다.');
                        return;
                    }

                    if (!window.confirm('이 응급상황을 종료하시겠습니까?')) {
                        return;
                    }

                    const result = await closeEmergencyAlert(alertId, token);

                    if (result.success) {
                        await loadEmergency();
                    } else {
                        window.alert(result.message || '상황종료 실패');
                    }
                }}
                onClose={() => setEmergencyModalType(null)}
            />
        )}
        {emergencyModalType === 'process' && (
            <ProcessModal
                data={emergencyEndList}
                onClose={() => setEmergencyModalType(null)}
            />
        )}
    </div>
  );

}

export default DashboardRedesign;
